/**
 * 设备记录面板组件 - 与 BudgetOverviewTower 风格一致
 */
import React, { useState, useEffect } from 'react';
import { Spin, Empty, message } from 'antd';
import { EquipmentUsageService } from '../../../services/equipmentUsageService';
import { formatMoney } from '../../../utils/budgetUtils';

const fmt = (n: number): string => {
  if (Math.abs(n) >= 10000) return `¥${(n / 10000).toFixed(1)}万`;
  return `¥${n.toLocaleString('zh-CN', { maximumFractionDigits: 2 })}`;
};

interface EquipmentPanelProps {
  projectId: string;
  isMobile?: boolean;
  active?: boolean;
  constructionType?: string;
}

interface EquipmentRecord {
  _id: string;
  usageDate: string;
  equipmentName: string;
  usageHours?: number;
  cost?: number;
  remarks?: string;
  constructionType?: string | null;
}

const EquipmentPanel: React.FC<EquipmentPanelProps> = ({
  projectId,
  isMobile = false,
  active = false,
  constructionType,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<EquipmentRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const fetchData = async () => {
    if (!projectId) return;

    setLoading(true);
    try {
      const response = await EquipmentUsageService.getEquipmentUsages({
        projectId,
        constructionType,
        page: 1,
        limit: 500
      });

      if (response.data) {
        setData(response.data);
        setTotal(response.pagination?.total || response.data.length);
      }
    } catch (error) {
      console.error('获取设备记录失败:', error);
      message.error('获取设备记录失败');
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  useEffect(() => {
    if (active && !loaded) {
      fetchData();
    }
  }, [active, loaded, projectId, constructionType]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    return dateStr.split('T')[0];
  };

  if (!loaded && !active) {
    return null;
  }

  if (loading && !data.length) {
    return (
      <div style={{
        textAlign: 'center', padding: '40px 0',
        color: 'var(--text-muted)', fontSize: 13,
      }}>
        <Spin size="small" /> 加载中...
      </div>
    );
  }

  if (!data.length) {
    return (
      <div style={{
        textAlign: 'center', padding: '40px 16px',
        color: 'var(--text-muted)',
      }}>
        <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.4 }}>{'🚜'}</div>
        <div style={{ fontSize: 13 }}>暂无设备记录</div>
      </div>
    );
  }

  return (
    <div>
      <style>{`
        .equipment-panel-row:hover { background: rgba(0,0,0,0.025) !important; }
      `}</style>

      <div style={{ borderRadius: '0 0 var(--radius-sm) var(--radius-sm)', overflow: 'hidden', maxHeight: 320, overflowY: 'auto' }}>
        {/* 表头 */}
        {!isMobile && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '90px 1fr 60px 70px 100px',
            gap: 8,
            padding: '6px 12px',
            background: 'rgba(0,0,0,0.03)',
            borderBottom: '1px solid var(--border)',
            alignItems: 'center',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.5 }}>日期</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.5 }}>设备</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.5 }}>施工类型</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.5, textAlign: 'right' }}>时长</span>
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: 0.5, textAlign: 'right' }}>金额</span>
          </div>
        )}
        {data.map((record, i) => (
          <div
            key={record._id}
            className="equipment-panel-row"
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr auto' : '90px 1fr 60px 70px 100px',
              gap: isMobile ? 0 : 8,
              padding: isMobile ? '10px 12px' : '10px 12px',
              borderBottom: i < data.length - 1 ? '1px solid var(--border)' : 'none',
              alignItems: 'center',
              transition: 'background 0.15s',
              cursor: 'default',
            }}
          >
            {isMobile ? (
              <>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 3 }}>
                    {record.equipmentName}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {formatDate(record.usageDate)} · {record.usageHours}小时
                  </div>
                  {record.remarks && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, fontStyle: 'italic' }}>
                      {record.remarks}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 13, fontFamily: '"JetBrains Mono", monospace', color: 'var(--text-primary)', textAlign: 'right', minWidth: 80 }}>
                  {fmt(record.cost || 0)}
                </div>
              </>
            ) : (
              <>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: '"JetBrains Mono", monospace' }}>
                  {formatDate(record.usageDate)}
                </span>
                <div>
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
                    {record.equipmentName}
                  </span>
                  {record.remarks && (
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
                      {record.remarks}
                    </div>
                  )}
                </div>
                <span style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                  {record.constructionType || '—'}
                </span>
                <span style={{ fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center', fontFamily: '"JetBrains Mono", monospace' }}>
                  {record.usageHours}h
                </span>
                <span style={{ fontSize: 13, color: 'var(--text-primary)', textAlign: 'right', fontFamily: '"JetBrains Mono", monospace', fontWeight: 600 }}>
                  {fmt(record.cost || 0)}
                </span>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Footer */}
      {total > 0 && (
        <div style={{
          display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
          padding: '8px 12px',
          borderTop: '1px solid var(--border)',
          background: 'var(--bg-surface)',
        }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            共 {total} 条，已显示 {data.length} 条
          </span>
        </div>
      )}
    </div>
  );
};

export default EquipmentPanel;
